import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import MessageTable from '../components/MessageTable';
import { useDeleteMessage } from '../hooks/useChatMutations';
import DynamicFilterBuilder from '@/shared/filtering/DynamicFilterBuilder';
import { ADMIN_FILTER_RESOURCES, type DynamicFilterGroup, type DynamicRelationLookups } from '@/shared/filtering/types';
import { useDynamicAdminList } from '@/shared/filtering/useDynamicAdminList';
import { chatApi } from '../api/chat.api';
import { userApi } from '@/features/users/api/user.api';

export default function MessageListPage() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const initialChatRoomId = searchParams.get('chatRoomId') || '';

  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const list = useDynamicAdminList<any>(ADMIN_FILTER_RESOURCES.chatMessages, {
    initialSortField: 'id',
    initialSortDir: 'desc',
  });
  const { data, isLoading } = list.searchQuery as any;
  const deleteMutation = useDeleteMessage();

  useEffect(() => {
    if (!initialChatRoomId || list.filters.conditions.length > 0) return;
    const next: DynamicFilterGroup = {
      logic: 'AND',
      conditions: [{ field: 'room.id', operator: 'EQUALS', value: Number(initialChatRoomId) }],
    };
    list.setFilters(next);
  }, [initialChatRoomId, list]);

  const relationLookups = useMemo<DynamicRelationLookups>(() => ({
    'room.id': {
      fetchFn: ({ page, size }) => chatApi.listRooms({ page, size }),
      mapOption: (r: any) => ({ value: r.id, label: `#${r.id}` }),
      placeholder: t('chat.chatRoomId'),
    },
    'sender.id': {
      fetchFn: ({ search, page, size }) => userApi.list({ page, size, firstName: search }),
      mapOption: (u: any) => ({ value: u.id, label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || `#${u.id}` }),
      placeholder: t('filters.senderId'),
    },
  }), [t]);

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSettled: () => setDeleteTarget(null) });
  };

  return (
    <div>
      <PageHeader
        title={t('chat.messages')}
        subtitle={initialChatRoomId ? t('chat.messagesForRoom', { id: initialChatRoomId }) : t('chat.messagesSubtitle')}
      />
      <DynamicFilterBuilder resource={list.resource} metadata={list.metadataQuery.data} value={list.filters} onChange={list.setFilters} onReset={list.resetFilters} relationLookups={relationLookups} />
      <div style={{ marginTop: 16 }}>
        <MessageTable
          data={data?.content || []}
          loading={isLoading}
          sortField={list.sortField}
          sortDir={list.sortDir}
          onSort={list.handleSort}
          onDelete={(item: any) => setDeleteTarget(item)}
          page={data?.currentPage ?? 0}
          pageSize={list.size}
          total={data?.totalItems ?? 0}
          onPageChange={list.setPage}
        />
      </div>
      <ConfirmDialog open={!!deleteTarget} title={t('chat.deleteMessage')}
        message={t('chat.deleteMessageConfirm', { id: deleteTarget?.id })}
        onConfirm={handleConfirmDelete} onCancel={() => setDeleteTarget(null)} loading={deleteMutation.isPending} />
    </div>
  );
}
